import type { Component } from 'vue'
import {
  AlarmClock, Archive, ArrowLeftRight, Award, Baby, BadgeCheck, Banknote, Bell, Bookmark, Boxes,
  Briefcase, Building, Building2, Calculator, Calendar, CalendarCheck, CalendarClock, CalendarDays,
  CalendarPlus, CalendarRange, CalendarX, Car, ChartColumn, ChartLine, ChartPie, Clipboard,
  ClipboardCheck, ClipboardList, Clock, Coffee, Coins, Contact, Cpu, CreditCard, DollarSign,
  Factory, FileClock, FileSearch, FileSpreadsheet, FileText, Fingerprint, Flag, FolderOpen, Gauge,
  Gift, Globe, GraduationCap, HandCoins, HardHat, Hammer, Hash, Heart, HeartPulse, History,
  Hourglass, House, IdCard, Inbox, KeyRound, Landmark, LayoutDashboard, LayoutGrid, Layers,
  Lightbulb, Link, List, ListChecks, ListOrdered, ListPlus, LogIn, LogOut, Mail, Map, MapPin,
  Megaphone, MessageSquare, Monitor, Moon, Navigation, Network, Package, Percent, PiggyBank, Pin,
  Plane, Plug, Presentation, QrCode, Receipt, Repeat, Rocket, Scale, ScanFace, ScrollText, Search,
  Send, Settings, Sheet, ShieldAlert, ShieldCheck, Sigma, SlidersHorizontal, Smartphone, SquarePen,
  Star, Store, Sun, Table, Tablet, Tag, Tags, Target, Timer, TrendingUp, TriangleAlert, Trophy,
  Truck, User, UserCheck, UserCog, UserPlus, UserX, Users, UsersRound, Wallet, Warehouse, Watch,
  Wifi, Wrench, Zap
} from '@lucide/vue'

/**
 * The icons a sidebar item can wear, keyed by the name stored in a person's sidebar layout.
 *
 * A stored layout refers to icons by these names, so a key must never be renamed or removed once
 * shipped -- only added. Lucide's own export names are used as keys so they stay recognisable,
 * always the canonical spelling (ChartColumn, not its BarChart3 alias).
 *
 * Imported one by one rather than as the whole set so the bundle only carries what is offered.
 */
export const NAV_ICON_GROUPS: { label: string, icons: Record<string, Component> }[] = [
  {
    label: 'Time & attendance',
    icons: { Clock, Timer, AlarmClock, Hourglass, History, CalendarClock, Watch, Fingerprint, ScanFace, LogIn, LogOut, SquarePen }
  },
  {
    label: 'Scheduling',
    icons: { Calendar, CalendarDays, CalendarCheck, CalendarRange, CalendarX, CalendarPlus, Repeat, Coffee, Sun, Moon, Plane }
  },
  {
    label: 'People',
    icons: { User, Users, UsersRound, UserPlus, UserCheck, UserX, UserCog, Contact, IdCard, BadgeCheck, GraduationCap, HeartPulse, Baby }
  },
  {
    label: 'Money & payroll',
    icons: { Coins, CreditCard, Wallet, Banknote, Receipt, PiggyBank, Landmark, Calculator, Percent, DollarSign, HandCoins, Gift }
  },
  {
    label: 'Reports & data',
    icons: { ChartColumn, ChartLine, ChartPie, TrendingUp, Sigma, Table, Sheet, FileText, FileSpreadsheet, FileSearch, FileClock, ClipboardList, Clipboard, ClipboardCheck, List, ListChecks, ListOrdered, ListPlus, Gauge }
  },
  {
    label: 'Places & devices',
    icons: { MapPin, Map, Navigation, Globe, House, Building, Building2, Store, Factory, Warehouse, Truck, Car, Cpu, Tablet, Smartphone, Monitor, QrCode, Wifi, Plug }
  },
  {
    label: 'Work & operations',
    icons: { Briefcase, Wrench, Hammer, HardHat, ShieldCheck, ShieldAlert, TriangleAlert, Scale, Flag, Tag, Tags, Hash, Layers, Network, FolderOpen, Archive, Package, Boxes, ArrowLeftRight, KeyRound }
  },
  {
    label: 'General',
    icons: { LayoutDashboard, LayoutGrid, Bell, Mail, Send, Inbox, MessageSquare, Megaphone, Presentation, ScrollText, Star, Heart, Bookmark, Pin, Zap, Rocket, Target, Award, Trophy, Lightbulb, Settings, SlidersHorizontal, Search, Link }
  }
]

export const NAV_ICONS: Record<string, Component> = Object.assign({}, ...NAV_ICON_GROUPS.map(group => group.icons))

export const FALLBACK_NAV_ICON = 'FileText'

export function isNavIconName (name: unknown): name is string {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(NAV_ICONS, name)
}

/** The component for a stored icon name. Unknown names get a plain page icon rather than nothing. */
export function resolveNavIcon (name: unknown): Component {
  return isNavIconName(name) ? NAV_ICONS[name] : NAV_ICONS[FALLBACK_NAV_ICON]
}
